import type { TicketListSocketItem } from '@21k/shared';
import { DepartmentRole, SystemModule } from '@21k/shared';

import type { TicketListItem, UserProfile } from '../api/types';
import { canReadModule, isSuperAdmin } from './permissions';

export type TicketListFilters = {
  departmentId: string;
  statusId: string;
  categoryId: string;
  assignedToMe: boolean;
  teamId: string;
  ownershipDepartmentId: string;
  search: string;
};

export function socketItemToListItem(ticket: TicketListSocketItem): TicketListItem {
  const { ticketNumber: _ticketNumber, ...rest } = ticket;
  return rest as TicketListItem;
}

export function userSeesAllTicketListEvents(user: UserProfile): boolean {
  if (isSuperAdmin(user.role)) return true;
  if (canReadModule(user, SystemModule.TICKET_MANAGEMENT)) return true;
  return user.departmentMemberships.some((m) => m.role === DepartmentRole.DEPARTMENT_ADMIN);
}

export function userCanAcceptLiveTicket(user: UserProfile, ticket: TicketListSocketItem): boolean {
  if (userSeesAllTicketListEvents(user)) return true;
  if (ticket.assignedToId === user.id) return true;
  if (ticket.createdById === user.id) return true;
  return user.departmentMemberships.some((m) => m.departmentId === ticket.departmentId);
}

export function ticketMatchesListFilters(
  ticket: TicketListSocketItem,
  filters: TicketListFilters,
  userId: string,
): boolean {
  if (filters.departmentId && ticket.departmentId !== filters.departmentId) return false;
  if (filters.statusId && ticket.statusId !== filters.statusId) return false;
  if (filters.categoryId && ticket.categoryId !== filters.categoryId) return false;
  if (
    filters.assignedToMe &&
    ticket.assignedToId !== userId &&
    ticket.createdById !== userId
  ) {
    return false;
  }
  if (filters.teamId) return false;
  if (filters.ownershipDepartmentId) return false;

  const q = filters.search.trim().toLowerCase();
  if (!q) return true;

  const haystack = [
    ticket.title,
    ticket.id,
    ticket.contactName,
    ticket.contactEmail,
    ticket.contactPhone,
    ticket.linkedLearnerName,
    ticket.linkedEnrollmentId,
    ticket.ticketNumber,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(q);
}
